"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import styles from "@/components/news/NewsWorkspace.module.css";
import type { BusinessHeadline } from "@/lib/tribuneBusinessFeed";

const THEME_RULES = [
  {
    label: "PSX & Markets",
    terms: ["psx", "market", "markets", "share", "shares", "stock", "stocks", "kse", "index", "broker"],
  },
  {
    label: "Macro & Policy",
    terms: ["economy", "economic", "sbp", "inflation", "interest", "budget", "imf", "policy", "rupee", "forex", "tax"],
  },
  {
    label: "Banking & Finance",
    terms: ["bank", "banking", "finance", "financial", "lending", "deposit", "loan"],
  },
  {
    label: "Energy & Commodities",
    terms: ["energy", "oil", "gas", "power", "electricity", "petroleum", "commodity"],
  },
  {
    label: "Corporate",
    terms: ["company", "corporate", "profit", "earnings", "dividend", "acquisition", "merger", "factory"],
  },
] as const;

function formatPublishedAt(value: string) {
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(asDate);
}

function getHeadlineThemes(item: BusinessHeadline) {
  const haystack = [item.title, item.summary, item.content_preview, item.categories.join(" ")]
    .join(" ")
    .toLowerCase();

  const themes = THEME_RULES.filter((theme) => theme.terms.some((term) => haystack.includes(term))).map((theme) => theme.label);
  return themes.length > 0 ? themes : ["Business Watch"];
}

function getPrimaryTheme(item: BusinessHeadline) {
  return getHeadlineThemes(item)[0];
}

function getExcerpt(item: BusinessHeadline) {
  return item.summary || item.content_preview || "Open the preview to inspect the headline context and jump to the original publisher.";
}

export default function BusinessNewsClient({
  initialHeadlines,
  initialError,
}: {
  initialHeadlines: BusinessHeadline[];
  initialError?: string | null;
}) {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All sources");
  const [themeFilter, setThemeFilter] = useState("All themes");

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    initialHeadlines.forEach((item) => {
      counts.set(item.source, (counts.get(item.source) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
  }, [initialHeadlines]);

  const themeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    initialHeadlines.forEach((item) => {
      getHeadlineThemes(item).forEach((theme) => {
        counts.set(theme, (counts.get(theme) ?? 0) + 1);
      });
    });
    return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
  }, [initialHeadlines]);

  const filteredHeadlines = useMemo(() => {
    const q = search.trim().toLowerCase();
    return initialHeadlines.filter((item) => {
      if (sourceFilter !== "All sources" && item.source !== sourceFilter) return false;
      if (themeFilter !== "All themes" && !getHeadlineThemes(item).includes(themeFilter)) return false;
      if (!q) return true;

      return (
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.author.toLowerCase().includes(q) ||
        item.categories.some((category) => category.toLowerCase().includes(q)) ||
        getHeadlineThemes(item).some((theme) => theme.toLowerCase().includes(q))
      );
    });
  }, [initialHeadlines, search, sourceFilter, themeFilter]);

  const leadHeadline = filteredHeadlines[0] ?? null;
  const secondaryHeadlines = filteredHeadlines.slice(1, 4);
  const remainingHeadlines = filteredHeadlines.slice(4);
  const dominantTheme = themeCounts[0]?.[0] ?? "Business Watch";

  return (
    <section className={styles.workspace}>
      <div className={styles.shell}>
        <div className={styles.heroGrid}>
          <article className={styles.leadCard}>
            <div className={styles.leadHeader}>
              <div className={styles.eyebrow}>News Desk</div>
              <div className={styles.eyebrowMuted}>Built for PSX and Pakistan business context</div>
            </div>

            {leadHeadline ? (
              <>
                <div className={styles.leadMeta}>
                  <span className={styles.metaPill}>{leadHeadline.source}</span>
                  <span className={styles.metaPill}>{formatPublishedAt(leadHeadline.published_at)}</span>
                  <span className={styles.metaPill}>{getPrimaryTheme(leadHeadline)}</span>
                </div>

                <h2 className={styles.leadTitle}>{leadHeadline.title}</h2>
                <p className={styles.leadSummary}>{getExcerpt(leadHeadline)}</p>

                <div className={styles.leadActions}>
                  <Link href={`/news/${leadHeadline.slug}`} className={styles.primaryLink}>
                    Open briefing
                  </Link>
                  <a href={leadHeadline.link} target="_blank" rel="noreferrer" className={styles.secondaryLink}>
                    Read original
                  </a>
                </div>
              </>
            ) : (
              <>
                <h2 className={styles.leadTitle}>Business and market coverage is syncing</h2>
                <p className={styles.leadSummary}>
                  Use the filters below to focus by source, theme, or keyword once the feed is available.
                </p>
              </>
            )}
          </article>

          <aside className={styles.sidePanel}>
            <section className={styles.panelSection}>
              <div className={styles.panelHeading}>Coverage Pulse</div>
              <div className={styles.metricGrid}>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Total Headlines</div>
                  <div className={styles.metricValue}>{initialHeadlines.length}</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Active Sources</div>
                  <div className={styles.metricValue}>{sourceCounts.length}</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Top Theme</div>
                  <div className={styles.metricValue}>{dominantTheme}</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>Current Filter</div>
                  <div className={styles.metricValue}>{themeFilter === "All themes" ? "All themes" : themeFilter}</div>
                </div>
              </div>
            </section>

            <section className={styles.panelSection}>
              <div className={styles.panelHeading}>Source Mix</div>
              <div className={styles.sourceStack}>
                {sourceCounts.map(([source, count]) => (
                  <div key={source} className={styles.sourceRow}>
                    <span className={styles.sourceName}>{source}</span>
                    <span className={styles.sourceCount}>{count} stories</span>
                  </div>
                ))}
              </div>
            </section>

            <section className={styles.panelSection}>
              <div className={styles.panelHeading}>Coverage Themes</div>
              <div className={styles.themeRow}>
                {themeCounts.slice(0, 6).map(([theme]) => (
                  <span key={theme} className={styles.themeTag}>
                    {theme}
                  </span>
                ))}
              </div>
            </section>
          </aside>
        </div>

        <section className={styles.toolbar}>
          <div className={styles.toolbarTop}>
            <div className={styles.toolbarTitle}>Filter stories</div>
            <div className={styles.toolbarMeta}>{filteredHeadlines.length} of {initialHeadlines.length} visible</div>
          </div>

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search PSX, budget, SBP, banking, energy..."
            className={styles.searchInput}
          />

          <div className={styles.chipGroup}>
            <button
              type="button"
              className={`${styles.filterChip} ${sourceFilter === "All sources" ? styles.filterChipActive : ""}`}
              onClick={() => setSourceFilter("All sources")}
            >
              All sources
            </button>
            {sourceCounts.map(([source, count]) => (
              <button
                key={source}
                type="button"
                className={`${styles.filterChip} ${sourceFilter === source ? styles.filterChipActive : ""}`}
                onClick={() => setSourceFilter(source)}
              >
                {source}
                <span className={styles.chipCount}>{count}</span>
              </button>
            ))}
          </div>

          <div className={styles.chipGroup}>
            <button
              type="button"
              className={`${styles.filterChip} ${themeFilter === "All themes" ? styles.filterChipActive : ""}`}
              onClick={() => setThemeFilter("All themes")}
            >
              All themes
            </button>
            {themeCounts.slice(0, 7).map(([theme, count]) => (
              <button
                key={theme}
                type="button"
                className={`${styles.filterChip} ${themeFilter === theme ? styles.filterChipActive : ""}`}
                onClick={() => setThemeFilter(theme)}
              >
                {theme}
                <span className={styles.chipCount}>{count}</span>
              </button>
            ))}
          </div>

          {initialError ? (
            <div className={styles.statusBanner}>
              One or more external feeds did not load cleanly. The desk is still usable, but current coverage may be partial. Details: {initialError}
            </div>
          ) : null}
        </section>

        {secondaryHeadlines.length > 0 ? (
          <div className={styles.secondaryGrid}>
            {secondaryHeadlines.map((item) => (
              <article key={item.id} className={styles.secondaryCard}>
                <div className={styles.leadMeta}>
                  <span className={styles.metaPill}>{item.source}</span>
                  <span className={styles.metaPill}>{getPrimaryTheme(item)}</span>
                </div>
                <h3 className={styles.secondaryTitle}>{item.title}</h3>
                <p className={styles.secondarySummary}>{getExcerpt(item).slice(0, 180)}</p>
                <div className={styles.articleActions}>
                  <Link href={`/news/${item.slug}`} className={styles.ghostLink}>
                    Open preview
                  </Link>
                  <a href={item.link} target="_blank" rel="noreferrer" className={styles.secondaryLink}>
                    Original
                  </a>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        <section className={styles.feedHeader}>
          <div className={styles.feedTitle}>All stories</div>
          <div className={styles.toolbarMeta}>
            <a href="https://tribune.com.pk/feed/business" target="_blank" rel="noreferrer" style={{ color: "var(--color-primary)" }}>
              Tribune Business
            </a>
            {" · "}
            <a href="https://www.brecorder.com/feeds/latest-news" target="_blank" rel="noreferrer" style={{ color: "var(--color-primary)" }}>
              Business Recorder
            </a>
          </div>
        </section>

        {filteredHeadlines.length === 0 ? (
          <div className={styles.emptyState}>
            No stories match the current search and filters. Try clearing the source or theme chips to widen the desk again.
          </div>
        ) : (
          <div className={styles.articleGrid}>
            {(remainingHeadlines.length > 0 ? remainingHeadlines : filteredHeadlines).map((item) => (
              <article key={item.id} className={styles.articleCard}>
                <div className={styles.articleTop}>
                  <span className={styles.sourceBadge}>{item.source}</span>
                  <span className={styles.publishedAt}>{formatPublishedAt(item.published_at)}</span>
                </div>
                <h3 className={styles.articleTitle}>{item.title}</h3>
                <p className={styles.articleSummary}>{item.summary || getPrimaryTheme(item)}</p>
                <div className={styles.categoryRow}>
                  {[getPrimaryTheme(item), ...item.categories].slice(0, 2).map((category) => (
                    <span key={`${item.id}-${category}`} className={styles.categoryChip}>
                      {category}
                    </span>
                  ))}
                </div>
                <div className={styles.articleActions}>
                  <Link href={`/news/${item.slug}`} className={styles.primaryLink}>
                    View preview
                  </Link>
                  <a href={item.link} target="_blank" rel="noreferrer" className={styles.ghostLink}>
                    Read original
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
